import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api, type Racecard } from "@/api";
import { presentBetSlipError } from "@/betslip/presentation";
import { Button, odds } from "@/components/ui";

import type {
  ExoticBet,
  HomeBettingCardProps,
  StraightBet,
} from "./HomeBettingCard";

const STRAIGHT = ["win", "place", "show"] as const;
const EXOTICS = ["exacta", "quinella", "trifecta"] as const;
const DESCRIPTIONS = {
  win: "Finish first",
  place: "Finish in the top two",
  show: "Finish in the top three",
  exacta: "Pick two horses in finishing order.",
  quinella: "Pick two horses in either order.",
  trifecta: "Pick three horses in finishing order.",
};
const RANKS = ["1st", "2nd", "3rd"];
const title = (value: string) => value[0].toUpperCase() + value.slice(1);
const safeOdds = (value: number | undefined): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0;

type Props = Pick<HomeBettingCardProps, "onBet" | "onAddExotic"> & {
  card: Racecard;
  market: Awaited<ReturnType<typeof api.markets>>;
  open: boolean;
  active: boolean;
};

export function MarketBet({
  card,
  market,
  open,
  active,
  onBet,
  onAddExotic,
}: Props) {
  const [type, setType] = useState<StraightBet | ExoticBet>("win");
  const straight = type === "win" || type === "place" || type === "show";
  return (
    <>
      <div className="market-panel__bet-types">
        {[STRAIGHT, EXOTICS].map((types, index) => (
          <div key={index}>
            {index === 1 ? (
              <span className="market-panel__group-label">Exotics</span>
            ) : null}
            <div
              className="market-panel__segments"
              role="group"
              aria-label={index === 0 ? "Straight bets" : "Exotic bets"}
            >
              {types.map((value) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={type === value}
                  onClick={() => setType(value)}
                >
                  {title(value)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {straight ? (
        <>
          <div className="market-panel__columns">
            <span>{DESCRIPTIONS[type]}</span>
            <span>Decimal odds</span>
          </div>
          <ol className="market-panel__runners">
            {card.runners.map((runner) => {
              const quote = market.board[type]?.[runner.gate];
              const priced = safeOdds(quote);
              return (
                <li className="market-panel__runner" key={runner.horseId}>
                  <span
                    className="market-panel__gate"
                    style={{ borderLeftColor: runner.silk }}
                  >
                    {runner.gate + 1}
                  </span>
                  <span className="market-panel__name">{runner.name}</span>
                  <button
                    className="market-panel__odds"
                    type="button"
                    disabled={!open || !priced}
                    aria-label={`${runner.name} to ${type}, ${priced ? odds(quote) : "odds unavailable"}`}
                    onClick={(event) => {
                      if (active && open && priced)
                        onBet(runner.gate, type, event.currentTarget);
                    }}
                  >
                    {priced ? odds(quote) : "—"}
                  </button>
                </li>
              );
            })}
          </ol>
        </>
      ) : (
        <ExoticSelection
          key={type}
          card={card}
          type={type}
          open={open}
          active={active}
          onAdd={onAddExotic}
        />
      )}
    </>
  );
}

function ExoticSelection({
  card,
  type,
  open,
  active,
  onAdd,
}: {
  card: Racecard;
  type: ExoticBet;
  open: boolean;
  active: boolean;
  onAdd: HomeBettingCardProps["onAddExotic"];
}) {
  const size = type === "trifecta" ? 3 : 2;
  // Empty ordered slots stay empty on removal; other horses never move rank.
  const [slots, setSlots] = useState<Array<number | null>>(() =>
    Array.from({ length: size }, () => null),
  );
  const picked = slots.filter((gate): gate is number => gate !== null);
  const selection =
    type === "quinella" ? [...picked].sort((a, b) => a - b) : picked;
  const complete = selection.length === size;
  const quote = useQuery({
    queryKey: ["race", card.raceId, "market-panel-quote", type, selection],
    queryFn: () =>
      api.quoteCoupon({
        type: "single",
        stakeCents: 100,
        legs: [{ raceId: card.raceId, betType: type, sel: selection }],
      }),
    enabled: active && open && complete,
    retry: false,
  });
  const legs = quote.data?.legs;
  const leg = Array.isArray(legs) && legs.length === 1 ? legs[0] : undefined;
  // Never turn a stale, reordered, cross-race or closed response into a leg.
  // The quote is a price preview only; the global slip re-quotes for placement.
  const validQuote = Boolean(
    complete &&
    quote.data?.type === "single" &&
    leg?.raceId === card.raceId &&
    leg.betType === type &&
    leg.bettingOpen === true &&
    Array.isArray(leg.sel) &&
    leg.sel.length === selection.length &&
    leg.sel.every((gate, index) => gate === selection[index]) &&
    safeOdds(leg.oddsH),
  );
  const ready =
    open && active && validQuote && !quote.isFetching && !quote.error;
  const failed = Boolean(quote.error || (quote.data && !validQuote));

  function toggle(gate: number) {
    setSlots((current) => {
      if (current.includes(gate))
        return current.map((value) => (value === gate ? null : value));
      if (!open || !active) return current;
      const emptyAt = current.indexOf(null);
      return emptyAt < 0
        ? current
        : current.map((value, index) => (index === emptyAt ? gate : value));
    });
  }

  return (
    <div className="market-panel__exotic">
      <p className="market-panel__hint">
        {DESCRIPTIONS[type]} Remove a pick to change it.
      </p>
      <ul
        className="market-panel__slots"
        aria-label={`${title(type)} selections`}
      >
        {slots.map((gate, index) => {
          const runner = card.runners.find((row) => row.gate === gate);
          return (
            <li key={index}>
              {type !== "quinella" ? <b>{RANKS[index]}</b> : null}
              {runner ? (
                <button
                  type="button"
                  onClick={() => toggle(runner.gate)}
                  aria-label={`Remove ${runner.name}${type === "quinella" ? "" : ` from ${RANKS[index]}`}`}
                >
                  <span>
                    {runner.gate + 1}. {runner.name}
                  </span>
                  <span aria-hidden>×</span>
                </button>
              ) : (
                <span>Choose a horse</span>
              )}
            </li>
          );
        })}
      </ul>
      <ol className="market-panel__runners">
        {card.runners.map((runner) => {
          const selectedAt = slots.indexOf(runner.gate);
          const selected = selectedAt >= 0;
          return (
            <li key={runner.horseId}>
              <button
                className="market-panel__pick"
                type="button"
                aria-label={`${selected ? "Remove" : "Select"} ${runner.name}`}
                aria-pressed={selected}
                disabled={!selected && (!open || complete)}
                onClick={() => toggle(runner.gate)}
              >
                <span
                  className="market-panel__gate"
                  style={{ borderLeftColor: runner.silk }}
                >
                  {runner.gate + 1}
                </span>
                <span className="market-panel__name">{runner.name}</span>
                <b>
                  {selected
                    ? type === "quinella"
                      ? "Selected"
                      : RANKS[selectedAt]
                    : "+"}
                </b>
              </button>
            </li>
          );
        })}
      </ol>
      <div className="market-panel__quote">
        <div aria-live="polite">
          <span>
            {picked.length}/{size} selected
          </span>
          <strong>{ready ? `${odds(leg!.oddsH)} decimal` : "—"}</strong>
        </div>
        {complete && open && quote.isFetching ? (
          <p role="status">Updating quote…</p>
        ) : null}
        {complete && open && failed && !quote.isFetching ? (
          <div role="alert">
            <p>
              {quote.error
                ? presentBetSlipError(quote.error, "quote").message
                : "The quote no longer matches this open selection. Please retry."}
            </p>
            <Button variant="secondary" onClick={() => void quote.refetch()}>
              Retry quote
            </Button>
          </div>
        ) : null}
        <Button
          disabled={!ready}
          onClick={(event) => {
            if (ready && leg)
              onAdd(type, [...selection], leg.oddsH, event.currentTarget);
          }}
        >
          Add to betslip
        </Button>
        <small>
          Price preview only. Set your stake and confirm in the betslip.
        </small>
      </div>
    </div>
  );
}
